// Issues.jsx - Issue Tracker Page
// Version 1.0.0

import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Plus, Search, Edit, Trash2, X, Check, Loader2,
  AlertCircle, Bug, Sparkles, TrendingUp, HelpCircle, CheckSquare,
  MessageSquare, Paperclip, Filter, ChevronDown, Clock, Tag,
  AlertTriangle, Circle, ArrowUp, ArrowDown, Upload, Download,
  Eye, RefreshCw, Send, Image, FileText, Film, Music, File
} from 'lucide-react'
import { issuesApi, userApi } from '../services/api'
import { useAuth } from '../context/AuthContext'
import './Issues.css'

// Constants
const CATEGORIES = [
  { value: 'transcription', label: 'Transcription', color: '#8b5cf6' },
  { value: 'bot_performance', label: 'Bot Performance', color: '#ef4444' },
  { value: 'bot_optimization', label: 'Bot Optimization', color: '#f97316' },
  { value: 'dashboard', label: 'Dashboard', color: '#3b82f6' },
  { value: 'monitoring', label: 'Monitoring', color: '#06b6d4' },
  { value: 'stats', label: 'Stats', color: '#22c55e' },
  { value: 'api', label: 'API', color: '#ec4899' },
  { value: 'infrastructure', label: 'Infrastructure', color: '#6b7280' },
  { value: 'other', label: 'Other', color: '#9ca3af' }
]

const TYPES = [
  { value: 'bug', label: 'Bug', icon: Bug, color: '#ef4444' },
  { value: 'feature', label: 'Feature', icon: Sparkles, color: '#8b5cf6' },
  { value: 'improvement', label: 'Improvement', icon: TrendingUp, color: '#3b82f6' },
  { value: 'question', label: 'Question', icon: HelpCircle, color: '#f59e0b' },
  { value: 'task', label: 'Task', icon: CheckSquare, color: '#22c55e' }
]

const PRIORITIES = [
  { value: 'critical', label: 'Critical', color: '#dc2626' },
  { value: 'high', label: 'High', color: '#f97316' },
  { value: 'medium', label: 'Medium', color: '#eab308' },
  { value: 'low', label: 'Low', color: '#22c55e' }
]

const STATUSES = [
  { value: 'open', label: 'Open', color: '#3b82f6' },
  { value: 'in_progress', label: 'In Progress', color: '#8b5cf6' },
  { value: 'review', label: 'Review', color: '#06b6d4' },
  { value: 'resolved', label: 'Resolved', color: '#22c55e' },
  { value: 'closed', label: 'Closed', color: '#6b7280' },
  { value: 'wontfix', label: "Won't Fix", color: '#6b7280' }
]

function Issues() {
  const navigate = useNavigate()
  const { user } = useAuth()

  // Data
  const [issues, setIssues] = useState([])
  const [labels, setLabels] = useState([])
  const [stats, setStats] = useState(null)
  const [pagination, setPagination] = useState({ total: 0, page: 1, pages: 1 })

  // Filters
  const [statusFilter, setStatusFilter] = useState('open,in_progress,review')
  const [priorityFilter, setPriorityFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [typeFilter, setTypeFilter] = useState('')
  const [searchTerm, setSearchTerm] = useState('')

  // UI State
  const [loading, setLoading] = useState(true)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedIssue, setSelectedIssue] = useState(null)
  const [saving, setSaving] = useState(false)

  // Create Form
  const [createForm, setCreateForm] = useState({
    title: '',
    description: '',
    type: 'bug',
    priority: 'medium',
    category: 'other'
  })

  // Comments
  const [comments, setComments] = useState([])
  const [newComment, setNewComment] = useState('')
  const [loadingComments, setLoadingComments] = useState(false)

  // File uploads
  const [pendingFiles, setPendingFiles] = useState([])
  const [commentFiles, setCommentFiles] = useState([])
  const [uploading, setUploading] = useState(false)

  // Activity
  const [activity, setActivity] = useState([])

  useEffect(() => {
    loadIssues()
    loadLabels()
    loadStats()
  }, [statusFilter, priorityFilter, categoryFilter, typeFilter, searchTerm])

  // ========== DATA LOADING ==========
  const loadIssues = async () => {
    setLoading(true)
    try {
      const params = {
        status: statusFilter || undefined,
        priority: priorityFilter || undefined,
        category: categoryFilter || undefined,
        type: typeFilter || undefined,
        search: searchTerm || undefined,
        limit: 50
      }
      const res = await issuesApi.getAll(params)
      setIssues(res.data.issues || [])
      setPagination(res.data.pagination || { total: 0, page: 1, pages: 1 })
    } catch (error) {
      console.error('Failed to load issues:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadLabels = async () => {
    try {
      const res = await issuesApi.getLabels()
      setLabels(res.data || [])
    } catch (error) {
      console.error('Failed to load labels:', error)
    }
  }

  const loadStats = async () => {
    try {
      const res = await issuesApi.getStats()
      setStats(res.data)
    } catch (error) {
      console.error('Failed to load stats:', error)
    }
  }

  const loadIssueDetails = async (issueId) => {
    try {
      const res = await issuesApi.getById(issueId)
      setSelectedIssue(res.data)

      // Load comments
      setLoadingComments(true)
      const commentsRes = await issuesApi.getComments(issueId)
      setComments(commentsRes.data || [])

      // Load activity
      const activityRes = await issuesApi.getActivity(issueId)
      setActivity(activityRes.data || [])
    } catch (error) {
      console.error('Failed to load issue details:', error)
    } finally {
      setLoadingComments(false)
    }
  }

  // ========== ACTIONS ==========
  const handleCreateIssue = async () => {
    if (!createForm.title.trim()) return

    setSaving(true)
    try {
      const res = await issuesApi.create({
        ...createForm,
        reporter_id: user?.id || 1
      })
      const newIssueId = res.data.id

      // Upload any pending files
      if (pendingFiles.length > 0 && newIssueId) {
        setUploading(true)
        for (const file of pendingFiles) {
          try {
            await issuesApi.uploadAttachment(newIssueId, file, user?.id)
          } catch (err) {
            console.error('Failed to upload file:', file.name, err)
          }
        }
        setUploading(false)
      }

      setShowCreateModal(false)
      setCreateForm({ title: '', description: '', type: 'bug', priority: 'medium', category: 'other' })
      setPendingFiles([])
      loadIssues()
      loadStats()
    } catch (error) {
      console.error('Failed to create issue:', error)
      alert('Failed to create issue')
    } finally {
      setSaving(false)
    }
  }

  const handleUpdateStatus = async (issueId, newStatus) => {
    try {
      await issuesApi.updateStatus(issueId, newStatus, user?.id)
      loadIssues()
      loadStats()
      if (selectedIssue?.id === issueId) {
        loadIssueDetails(issueId)
      }
    } catch (error) {
      console.error('Failed to update status:', error)
    }
  }

  const handleAddComment = async () => {
    if ((!newComment.trim() && commentFiles.length === 0) || !selectedIssue) return

    try {
      // Add the comment first (if there's text)
      if (newComment.trim()) {
        await issuesApi.addComment(selectedIssue.id, newComment, user?.id || 1)
      }

      // Upload any comment files
      if (commentFiles.length > 0) {
        setUploading(true)
        for (const file of commentFiles) {
          try {
            await issuesApi.uploadAttachment(selectedIssue.id, file, user?.id)
          } catch (err) {
            console.error('Failed to upload file:', file.name, err)
          }
        }
        setUploading(false)
      }

      setNewComment('')
      setCommentFiles([])
      loadIssueDetails(selectedIssue.id)
    } catch (error) {
      console.error('Failed to add comment:', error)
    }
  }

  // File handling
  const handleFileSelect = (e, type) => {
    const files = Array.from(e.target.files)
    if (type === 'create') {
      setPendingFiles(prev => [...prev, ...files])
    } else {
      setCommentFiles(prev => [...prev, ...files])
    }
    e.target.value = '' // Reset input
  }

  const removeFile = (index, type) => {
    if (type === 'create') {
      setPendingFiles(prev => prev.filter((_, i) => i !== index))
    } else {
      setCommentFiles(prev => prev.filter((_, i) => i !== index))
    }
  }

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase()
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg'].includes(ext)) return Image
    if (['mp4', 'webm', 'mov', 'avi'].includes(ext)) return Film
    if (['mp3', 'wav', 'ogg', 'm4a'].includes(ext)) return Music
    if (['pdf', 'doc', 'docx', 'txt'].includes(ext)) return FileText
    return File
  }

  const isImageFile = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase()
    return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)
  }

  const isVideoFile = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase()
    return ['mp4', 'webm', 'mov'].includes(ext)
  }

  const isAudioFile = (filename) => {
    const ext = filename.split('.').pop()?.toLowerCase()
    return ['mp3', 'wav', 'ogg', 'm4a'].includes(ext)
  }

  const handleDeleteIssue = async (issueId) => {
    if (!confirm('Are you sure you want to delete this issue?')) return

    try {
      await issuesApi.delete(issueId)
      setShowDetailModal(false)
      setSelectedIssue(null)
      loadIssues()
      loadStats()
    } catch (error) {
      console.error('Failed to delete issue:', error)
    }
  }

  const openIssueDetail = (issue) => {
    setSelectedIssue(issue)
    setShowDetailModal(true)
    loadIssueDetails(issue.id)
  }

  // ========== HELPERS ==========
  const getTypeIcon = (type) => {
    const typeConfig = TYPES.find(t => t.value === type)
    const Icon = typeConfig?.icon || Bug
    return <Icon size={16} style={{ color: typeConfig?.color }} />
  }

  const getPriorityColor = (priority) => {
    return PRIORITIES.find(p => p.value === priority)?.color || '#6b7280'
  }

  const getStatusColor = (status) => {
    return STATUSES.find(s => s.value === status)?.color || '#6b7280'
  }

  const getCategoryColor = (category) => {
    return CATEGORIES.find(c => c.value === category)?.color || '#6b7280'
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return ''
    // Parse MySQL datetime as UTC by appending 'Z' if no timezone info
    const utcDateStr = dateStr.includes('Z') || dateStr.includes('+') ? dateStr : dateStr.replace(' ', 'T') + 'Z'
    const date = new Date(utcDateStr)
    const now = new Date()
    const diff = now - date
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(hours / 24)

    if (minutes < 1) return 'just now'
    if (minutes < 60) return `${minutes}m ago`
    if (hours < 24) return `${hours}h ago`
    if (days < 7) return `${days}d ago`
    return date.toLocaleDateString()
  }

  const formatActivityAction = (action, oldVal, newVal) => {
    switch (action) {
      case 'created': return 'created this issue'
      case 'status_changed': return `changed status from ${oldVal} to ${newVal}`
      case 'priority_changed': return `changed priority from ${oldVal} to ${newVal}`
      case 'category_changed': return `changed category from ${oldVal} to ${newVal}`
      case 'label_added': return `added label ${newVal}`
      case 'label_removed': return `removed label ${oldVal}`
      case 'commented': return 'added a comment'
      case 'attachment_added': return `attached ${newVal}`
      default: return action
    }
  }

  // ========== RENDER ==========
  return (
    <div className="issues-page">
      {/* Header */}
      <div className="issues-header">
        <div className="header-left">
          <h1><AlertCircle size={28} /> Issues</h1>
          <p className="subtitle">Track and manage bugs, features, and improvements</p>
        </div>
        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          <Plus size={18} /> New Issue
        </button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="stat-blocks">
          <div className="stat-block">
            <div className="stat-icon"><AlertCircle size={20} /></div>
            <div className="stat-content">
              <span className="stat-value">{stats.open_count || 0}</span>
              <span className="stat-label">Open Issues</span>
            </div>
          </div>
          <div className="stat-block">
            <div className="stat-icon"><Bug size={20} /></div>
            <div className="stat-content">
              <span className="stat-value">
                {stats.by_priority?.find(p => p.priority === 'critical')?.count || 0}
              </span>
              <span className="stat-label">Critical</span>
            </div>
          </div>
          <div className="stat-block">
            <div className="stat-icon"><Clock size={20} /></div>
            <div className="stat-content">
              <span className="stat-value">{stats.avg_resolution_hours || 0}h</span>
              <span className="stat-label">Avg Resolution</span>
            </div>
          </div>
          <div className="stat-block">
            <div className="stat-icon"><Check size={20} /></div>
            <div className="stat-content">
              <span className="stat-value">
                {(stats.by_status?.find(s => s.status === 'resolved')?.count || 0) +
                 (stats.by_status?.find(s => s.status === 'closed')?.count || 0)}
              </span>
              <span className="stat-label">Resolved</span>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="issues-filters">
        <div className="filter-tabs">
          <button
            className={statusFilter === 'open,in_progress,review' ? 'active' : ''}
            onClick={() => setStatusFilter('open,in_progress,review')}
          >
            Open
          </button>
          <button
            className={statusFilter === 'resolved,closed,wontfix' ? 'active' : ''}
            onClick={() => setStatusFilter('resolved,closed,wontfix')}
          >
            Closed
          </button>
          <button
            className={statusFilter === '' ? 'active' : ''}
            onClick={() => setStatusFilter('')}
          >
            All
          </button>
        </div>

        <div className="filter-dropdowns">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>

          <select
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option value="">All Priorities</option>
            {PRIORITIES.map(p => (
              <option key={p.value} value={p.value}>{p.label}</option>
            ))}
          </select>

          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option value="">All Types</option>
            {TYPES.map(t => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
        </div>

        <div className="search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search issues..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <button className="btn-icon" onClick={loadIssues} title="Refresh">
          <RefreshCw size={18} />
        </button>
      </div>

      {/* Issues List */}
      <div className="issues-list">
        {loading ? (
          <div className="loading-state">
            <Loader2 size={32} className="spin" />
            <span>Loading issues...</span>
          </div>
        ) : issues.length === 0 ? (
          <div className="empty-state">
            <AlertCircle size={48} />
            <h3>No issues found</h3>
            <p>Create a new issue to get started</p>
          </div>
        ) : (
          issues.map(issue => (
            <div
              key={issue.id}
              className="issue-card"
              onClick={() => openIssueDetail(issue)}
            >
              <div className="issue-type">
                {getTypeIcon(issue.type)}
              </div>
              <div className="issue-main">
                <div className="issue-title-row">
                  <span className="issue-id">#{issue.id}</span>
                  <span className="issue-title">{issue.title}</span>
                </div>
                <div className="issue-meta">
                  <span
                    className="issue-category"
                    style={{ backgroundColor: getCategoryColor(issue.category) + '20', color: getCategoryColor(issue.category) }}
                  >
                    {CATEGORIES.find(c => c.value === issue.category)?.label || issue.category}
                  </span>
                  <span
                    className="issue-priority"
                    style={{ color: getPriorityColor(issue.priority) }}
                  >
                    {issue.priority}
                  </span>
                  {issue.labels?.map(label => (
                    <span
                      key={label.id}
                      className="issue-label"
                      style={{ backgroundColor: label.color + '20', color: label.color }}
                    >
                      {label.name}
                    </span>
                  ))}
                  <span className="issue-comments">
                    <MessageSquare size={14} /> {issue.comment_count || 0}
                  </span>
                </div>
              </div>
              <div className="issue-right">
                <span
                  className="issue-status"
                  style={{ backgroundColor: getStatusColor(issue.status) + '20', color: getStatusColor(issue.status) }}
                >
                  {STATUSES.find(s => s.value === issue.status)?.label || issue.status}
                </span>
                <span className="issue-date">{formatDate(issue.created_at)}</span>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="pagination">
          <span>Page {pagination.page} of {pagination.pages} ({pagination.total} issues)</span>
        </div>
      )}

      {/* Create Issue Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowCreateModal(false)}
          >
            <motion.div
              className="modal create-issue-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2><Plus size={20} /> Create New Issue</h2>
                <button className="btn-close" onClick={() => setShowCreateModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body">
                <div className="form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    placeholder="Brief description of the issue"
                    value={createForm.title}
                    onChange={(e) => setCreateForm({ ...createForm, title: e.target.value })}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Type</label>
                    <select
                      value={createForm.type}
                      onChange={(e) => setCreateForm({ ...createForm, type: e.target.value })}
                    >
                      {TYPES.map(t => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Category</label>
                    <select
                      value={createForm.category}
                      onChange={(e) => setCreateForm({ ...createForm, category: e.target.value })}
                    >
                      {CATEGORIES.map(c => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Priority</label>
                    <select
                      value={createForm.priority}
                      onChange={(e) => setCreateForm({ ...createForm, priority: e.target.value })}
                    >
                      {PRIORITIES.map(p => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    placeholder="Describe the issue in detail..."
                    rows={6}
                    value={createForm.description}
                    onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                  />
                </div>

                {/* File Upload Section */}
                <div className="form-group">
                  <label>Attachments</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      id="create-file-input"
                      multiple
                      onChange={(e) => handleFileSelect(e, 'create')}
                      style={{ display: 'none' }}
                      accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                    />
                    <label htmlFor="create-file-input" className="file-upload-btn">
                      <Upload size={16} />
                      Add Files
                    </label>
                    <span className="file-hint">Images, videos, audio, PDFs (max 10MB each)</span>
                  </div>

                  {pendingFiles.length > 0 && (
                    <div className="pending-files">
                      {pendingFiles.map((file, idx) => {
                        const FileIcon = getFileIcon(file.name)
                        return (
                          <div key={idx} className="pending-file">
                            <FileIcon size={14} />
                            <span className="file-name">{file.name}</span>
                            <span className="file-size">({Math.round(file.size / 1024)}KB)</span>
                            <button
                              type="button"
                              className="remove-file"
                              onClick={() => removeFile(idx, 'create')}
                            >
                              <X size={12} />
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button className="btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button
                  className="btn-primary"
                  onClick={handleCreateIssue}
                  disabled={!createForm.title.trim() || saving}
                >
                  {saving ? <Loader2 size={16} className="spin" /> : <Check size={16} />}
                  Create Issue
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Issue Detail Modal */}
      <AnimatePresence>
        {showDetailModal && selectedIssue && (
          <motion.div
            className="modal-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowDetailModal(false)}
          >
            <motion.div
              className="modal issue-detail-modal"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <div className="issue-detail-title">
                  {getTypeIcon(selectedIssue.type)}
                  <span className="issue-id">#{selectedIssue.id}</span>
                  <h2>{selectedIssue.title}</h2>
                </div>
                <button className="btn-close" onClick={() => setShowDetailModal(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="modal-body issue-detail-body">
                <div className="issue-detail-main">
                  {/* Status Bar */}
                  <div className="issue-status-bar">
                    <span
                      className="current-status"
                      style={{ backgroundColor: getStatusColor(selectedIssue.status), color: '#fff' }}
                    >
                      {STATUSES.find(s => s.value === selectedIssue.status)?.label}
                    </span>
                    <span className="status-separator">Move to:</span>
                    {STATUSES.filter(s => s.value !== selectedIssue.status).map(s => (
                      <button
                        key={s.value}
                        className="status-btn"
                        style={{ borderColor: s.color, color: s.color }}
                        onClick={() => handleUpdateStatus(selectedIssue.id, s.value)}
                      >
                        {s.label}
                      </button>
                    ))}
                  </div>

                  {/* Description */}
                  <div className="issue-description">
                    <h3>Description</h3>
                    {selectedIssue.description ? (
                      <p>{selectedIssue.description}</p>
                    ) : (
                      <p className="no-description">No description provided</p>
                    )}
                  </div>

                  {/* Attachments */}
                  {selectedIssue.attachments?.length > 0 && (
                    <div className="issue-attachments">
                      <h3><Paperclip size={16} /> Attachments ({selectedIssue.attachments.length})</h3>
                      <div className="attachments-grid">
                        {selectedIssue.attachments.map(att => {
                          const url = issuesApi.getAttachmentUrl(selectedIssue.id, att.id)
                          const FileIcon = getFileIcon(att.original_name)

                          if (isImageFile(att.original_name)) {
                            return (
                              <div key={att.id} className="attachment-preview image-preview">
                                <a href={url} target="_blank" rel="noopener noreferrer">
                                  <img src={url} alt={att.original_name} />
                                </a>
                                <div className="attachment-info">
                                  <span className="att-name">{att.original_name}</span>
                                  <span className="att-size">{Math.round(att.file_size / 1024)}KB</span>
                                </div>
                              </div>
                            )
                          }

                          if (isVideoFile(att.original_name)) {
                            return (
                              <div key={att.id} className="attachment-preview video-preview">
                                <video controls>
                                  <source src={url} />
                                </video>
                                <div className="attachment-info">
                                  <span className="att-name">{att.original_name}</span>
                                  <span className="att-size">{Math.round(att.file_size / 1024)}KB</span>
                                </div>
                              </div>
                            )
                          }

                          if (isAudioFile(att.original_name)) {
                            return (
                              <div key={att.id} className="attachment-preview audio-preview">
                                <div className="audio-icon"><Music size={24} /></div>
                                <audio controls>
                                  <source src={url} />
                                </audio>
                                <div className="attachment-info">
                                  <span className="att-name">{att.original_name}</span>
                                  <span className="att-size">{Math.round(att.file_size / 1024)}KB</span>
                                </div>
                              </div>
                            )
                          }

                          // Default file display
                          return (
                            <a
                              key={att.id}
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="attachment-preview file-preview"
                            >
                              <FileIcon size={24} />
                              <div className="attachment-info">
                                <span className="att-name">{att.original_name}</span>
                                <span className="att-size">{Math.round(att.file_size / 1024)}KB</span>
                              </div>
                              <Download size={14} className="download-icon" />
                            </a>
                          )
                        })}
                      </div>
                    </div>
                  )}

                  {/* Comments */}
                  <div className="issue-comments-section">
                    <h3><MessageSquare size={16} /> Comments ({comments.length})</h3>

                    {loadingComments ? (
                      <div className="loading-comments">
                        <Loader2 size={20} className="spin" />
                      </div>
                    ) : (
                      <div className="comments-list">
                        {comments.map(comment => (
                          <div key={comment.id} className="comment-item">
                            <div className="comment-header">
                              <span className="comment-author">{comment.user_name || 'Unknown'}</span>
                              <span className="comment-date">{formatDate(comment.created_at)}</span>
                            </div>
                            <div className="comment-content">{comment.content}</div>
                          </div>
                        ))}

                        {comments.length === 0 && (
                          <p className="no-comments">No comments yet</p>
                        )}
                      </div>
                    )}

                    {/* Add Comment */}
                    <div className="add-comment">
                      <textarea
                        placeholder="Write a comment or attach files..."
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        rows={3}
                      />

                      {/* Comment file upload */}
                      <div className="comment-actions">
                        <div className="comment-file-upload">
                          <input
                            type="file"
                            id="comment-file-input"
                            multiple
                            onChange={(e) => handleFileSelect(e, 'comment')}
                            style={{ display: 'none' }}
                            accept="image/*,video/*,audio/*,.pdf,.doc,.docx,.txt"
                          />
                          <label htmlFor="comment-file-input" className="attach-btn" title="Attach files">
                            <Paperclip size={16} />
                          </label>
                        </div>

                        <button
                          className="btn-primary"
                          onClick={handleAddComment}
                          disabled={(!newComment.trim() && commentFiles.length === 0) || uploading}
                        >
                          {uploading ? <Loader2 size={16} className="spin" /> : <Send size={16} />}
                          Send
                        </button>
                      </div>

                      {commentFiles.length > 0 && (
                        <div className="pending-files comment-pending">
                          {commentFiles.map((file, idx) => {
                            const FileIcon = getFileIcon(file.name)
                            return (
                              <div key={idx} className="pending-file">
                                <FileIcon size={14} />
                                <span className="file-name">{file.name}</span>
                                <span className="file-size">({Math.round(file.size / 1024)}KB)</span>
                                <button
                                  type="button"
                                  className="remove-file"
                                  onClick={() => removeFile(idx, 'comment')}
                                >
                                  <X size={12} />
                                </button>
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Sidebar */}
                <div className="issue-detail-sidebar">
                  <div className="sidebar-section">
                    <label>Category</label>
                    <span
                      className="category-badge"
                      style={{ backgroundColor: getCategoryColor(selectedIssue.category) + '20', color: getCategoryColor(selectedIssue.category) }}
                    >
                      {CATEGORIES.find(c => c.value === selectedIssue.category)?.label}
                    </span>
                  </div>

                  <div className="sidebar-section">
                    <label>Priority</label>
                    <span
                      className="priority-badge"
                      style={{ color: getPriorityColor(selectedIssue.priority) }}
                    >
                      <Circle size={10} fill={getPriorityColor(selectedIssue.priority)} />
                      {PRIORITIES.find(p => p.value === selectedIssue.priority)?.label}
                    </span>
                  </div>

                  <div className="sidebar-section">
                    <label>Type</label>
                    <span className="type-badge">
                      {getTypeIcon(selectedIssue.type)}
                      {TYPES.find(t => t.value === selectedIssue.type)?.label}
                    </span>
                  </div>

                  <div className="sidebar-section">
                    <label>Reporter</label>
                    <span>{selectedIssue.reporter_name || 'Unknown'}</span>
                  </div>

                  <div className="sidebar-section">
                    <label>Created</label>
                    <span>{new Date(selectedIssue.created_at).toLocaleString()}</span>
                  </div>

                  {selectedIssue.resolved_at && (
                    <div className="sidebar-section">
                      <label>Resolved</label>
                      <span>{new Date(selectedIssue.resolved_at).toLocaleString()}</span>
                    </div>
                  )}

                  {/* Labels */}
                  {selectedIssue.labels?.length > 0 && (
                    <div className="sidebar-section">
                      <label>Labels</label>
                      <div className="labels-list">
                        {selectedIssue.labels.map(label => (
                          <span
                            key={label.id}
                            className="label-badge"
                            style={{ backgroundColor: label.color + '20', color: label.color }}
                          >
                            {label.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Activity */}
                  <div className="sidebar-section activity-section">
                    <label>Activity</label>
                    <div className="activity-list">
                      {activity.slice(0, 10).map(item => (
                        <div key={item.id} className="activity-item">
                          <span className="activity-user">{item.user_name}</span>
                          <span className="activity-action">
                            {formatActivityAction(item.action, item.old_value, item.new_value)}
                          </span>
                          <span className="activity-time">{formatDate(item.created_at)}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Delete */}
                  <div className="sidebar-section danger-section">
                    <button
                      className="btn-danger"
                      onClick={() => handleDeleteIssue(selectedIssue.id)}
                    >
                      <Trash2 size={16} /> Delete Issue
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Issues
